// OrdersScreen.js
import React, {useCallback, useContext, useEffect, useMemo, useState} from 'react';
import {
    FlatList,
    Platform,
    ScrollView,
    Text,
    TextInput,
    TouchableOpacity,
    useWindowDimensions,
    View
} from 'react-native';
import {LinearGradient} from 'expo-linear-gradient';
import {OrdersContext} from '../helpers/OrdersContext';
import StatisticsHelper from '../helpers/StatisticsHelper';
import LottieView from 'lottie-react-native';
import OrderRow from '../components/OrderRow';
import {TableContext} from '../helpers/TableContext';
import {CartContext} from '../helpers/CartContext';
import AsyncStorage from '@react-native-async-storage/async-storage';
import {useTranslation} from 'react-i18next';
import {useFocusEffect} from '@react-navigation/native';
import {useTheme} from '../helpers/ThemeContext';
import {useThemedStyles} from '../helpers/useThemedStyles';
import {usePosLayout} from '../helpers/posLayout';
import {haptics} from '../helpers/haptics';
import {WINDOW_EXPANDED_MIN, WINDOW_MEDIUM_MIN} from '../helpers/windowSizeClass';

const safeJsonParse = (value, fallback) => {
    if (!value) {
        return fallback;
    }

    try {
        return JSON.parse(value);
    } catch (_error) {
        return fallback;
    }
};

const normalizeId = (value) => {
    if (!value) return '';
    if (typeof value === 'string') return value;
    if (typeof value === 'object') {
        return value.$oid || value._id || value.id || '';
    }
    return '';
};

const LIVE_ORDER_ROUTE_NAMES = new Set(['OpenTables', 'OrdersList', 'HomeOrdersList']);

const ORDER_TYPE_FILTERS = [
    {value: 'all', labelKey: 'orders_filter_all'},
    {value: 'takeAway', labelKey: 'orders_filter_take_away'},
    {value: 'dineIn', labelKey: 'orders_filter_dine_in'},
    {value: 'online', labelKey: 'orders_filter_online'},
];

const normalizeSearchText = (value = '') => String(value)
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();

const matchesOrderTypeFilter = (order, selectedFilter) => {
    if (selectedFilter === 'all') {
        return true;
    }

    return order?.orderType === selectedFilter;
};

const getOrderSearchBlob = (order) => {
    try {
        return normalizeSearchText(JSON.stringify(order));
    } catch (_error) {
        return normalizeSearchText(`${order?.orderCode || ''} ${order?.userInfo?.name || ''} ${order?.addressInfo?.address || ''}`);
    }
};

const getScreenCopy = (routeName, params = {}, t) => {
    if (params?.screenCopy) {
        return params.screenCopy;
    }

    if (params?.source === 'statistics') {
        return {
            eyebrow: t('orders_copy_statistics_eyebrow'),
            title: t('orders_copy_statistics_title'),
            subtitle: t('orders_copy_statistics_subtitle'),
            viewLabel: t('orders_copy_statistics_view'),
        };
    }

    switch (routeName) {
        case 'OpenTables':
            return {
                eyebrow: t('orders_copy_tables_eyebrow'),
                title: t('orders_copy_tables_title'),
                subtitle: t('orders_copy_tables_subtitle'),
                viewLabel: t('orders_copy_tables_view'),
            };
        case 'OrdersList':
            return {
                eyebrow: t('orders_copy_external_eyebrow'),
                title: t('orders_copy_external_title'),
                subtitle: t('orders_copy_external_subtitle'),
                viewLabel: t('orders_copy_external_view'),
            };
        case 'PendingOrdersScreen':
            return {
                eyebrow: t('orders_copy_pending_eyebrow'),
                title: t('orders_copy_pending_title'),
                subtitle: t('orders_copy_pending_subtitle'),
                viewLabel: t('orders_copy_pending_view'),
            };
        case 'HomeOrdersList':
        default:
            return {
                eyebrow: t('orders_copy_active_eyebrow'),
                title: t('orders_copy_active_title'),
                subtitle: t('orders_copy_active_subtitle'),
                viewLabel: t('orders_copy_active_view'),
            };
    }
};

export default function OrdersScreen({navigation, route}) {
    const {t} = useTranslation();
    const {theme, isDark} = useTheme();
    const styles = useThemedStyles(buildStyles);
    const {width} = useWindowDimensions();
    const {selectTable} = useContext(TableContext);
    const {orders, fetchOrdersFromAPI} = useContext(OrdersContext);
    // Phone UI: 48dp filter chips, a 52dp search field and pull-to-refresh.
    const {isPhoneUi} = usePosLayout();
    const [refreshing, setRefreshing] = useState(false);
    const handleRefresh = useCallback(async () => {
        setRefreshing(true);
        try {
            await fetchOrdersFromAPI?.();
            haptics.success();
        } catch (_error) {
            haptics.error();
        } finally {
            setRefreshing(false);
        }
    }, [fetchOrdersFromAPI]);
    // StatisticsHelper is an O(n·items) pass over the whole order list; memoize
    // it on `orders` so it only recomputes when the list actually changes, not
    // on every unrelated re-render of this screen.
    const {statistics} = useMemo(() => StatisticsHelper(orders), [orders]);

    // Refresh on focus so the open/pending orders list is current the instant
    // the operator opens it; the OrdersContext 10s polling floor keeps it live
    // thereafter regardless of realtime transport health.
    useFocusEffect(
        useCallback(() => {
            fetchOrdersFromAPI?.();
        }, [fetchOrdersFromAPI])
    );
    const {setAlreadySentCartItems, setDiscountsForWholeCart} = useContext(CartContext);
    const [restaurantPlace, setRestaurantPlace] = useState({name: '', raw: '', floors: [], _id: ''});
    const [selectedOrderType, setSelectedOrderType] = useState('all');
    const [searchTerm, setSearchTerm] = useState('');

    useEffect(() => {
        let mounted = true;

        const loadRestaurantPlace = async () => {
            const restaurantPlaceStored = await AsyncStorage.getItem('restaurantPlace');
            if (mounted) {
                setRestaurantPlace(safeJsonParse(restaurantPlaceStored, {name: '', raw: '', floors: [], _id: ''}));
            }
        };

        loadRestaurantPlace();

        return () => {
            mounted = false;
        };
    }, []);

    const statisticsOrders = useMemo(() => {
        if (!Array.isArray(route.params?.orders)) {
            return null;
        }

        const placeId = restaurantPlace?._id;

        return route.params.orders
            .filter((order) => (!placeId || order.restaurantPlace === placeId))
            .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
    }, [restaurantPlace?._id, route.params?.orders]);

    const moduleOrders = useMemo(() => {
        if (statisticsOrders) {
            return statisticsOrders;
        }

        const placeId = restaurantPlace?._id;
        if (!placeId) {
            return [];
        }

        if (route.name === 'OpenTables') {
            return (statistics.dineInOrders || [])
                .filter((order) => order.restaurantPlace === placeId)
                .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
        }

        if (route.name === 'OrdersList') {
            return [
                ...(statistics.onlineOrders || []),
                ...(statistics.unClosedTakeAwayOrders || []),
            ]
                .filter((order) => order.restaurantPlace === placeId)
                .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
        }

        if (route.name === 'PendingOrdersScreen') {
            const dayStart = new Date();
            dayStart.setHours(5, 0, 0, 0);

            return (statistics.allOrders || [])
                .filter((order) => (
                    order.restaurantPlace === placeId
                    && !order.storno
                    && order.status !== 'cancelled'
                    && order.status !== 'closed'
                    && order.preOrder
                    && new Date(order.preOrder) >= dayStart
                ))
                .sort((a, b) => new Date(a.preOrder) - new Date(b.preOrder));
        }

        return [
            ...(statistics.dineInOrders || []),
            ...(statistics.onlineOrders || []),
            ...(statistics.unClosedTakeAwayOrders || []),
        ]
            .filter((order) => (
                order.restaurantPlace === placeId
                && !order.storno
                && order.status !== 'cancelled'
                && order.status !== 'closed'
            ))
            .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
    }, [
        statisticsOrders,
        restaurantPlace?._id,
        route.name,
        statistics.allOrders,
        statistics.dineInOrders,
        statistics.onlineOrders,
        statistics.unClosedTakeAwayOrders,
    ]);

    const visibleOrders = useMemo(() => {
        const normalizedSearchTerm = normalizeSearchText(searchTerm);

        return moduleOrders.filter((order) => {
            if (!matchesOrderTypeFilter(order, selectedOrderType)) {
                return false;
            }

            if (!normalizedSearchTerm) {
                return true;
            }

            return getOrderSearchBlob(order).includes(normalizedSearchTerm);
        });
    }, [moduleOrders, searchTerm, selectedOrderType]);

    const handleOrderPress = useCallback((order) => {
        const hasNoFinalPayment = order?.paymentType === 'temporary' || !order?.paymentTypes || order.paymentTypes.length < 1;
        const shouldContinueInSaleScreen = route.params?.openMode !== 'details' && LIVE_ORDER_ROUTE_NAMES.has(route.name);

        if (hasNoFinalPayment && shouldContinueInSaleScreen) {
            const orderTableId = normalizeId(order?.tableId);
            const orderTable = restaurantPlace?.floors
                ?.flatMap((floor) => floor.elements || [])
                ?.find((element) => normalizeId(element?._id) === orderTableId);

            setAlreadySentCartItems(Array.isArray(order?.items) ? order.items : []);
            setDiscountsForWholeCart(order?.discounts || {amount: 0, percentage: 0});

            const fallbackTable = orderTableId
                ? {_id: orderTableId, name: order?.tableName || t('orders_table_fallback')}
                : null;
            const selectedTable = orderTable || fallbackTable;

            selectTable(selectedTable);
            navigation.navigate('HomeScreen', {orderData: order, table: selectedTable, saleScreen: true});
            return;
        }

        navigation.navigate('OrderDetailsScreen', {
            orderData: order,
            orderId: order._id,
            source: route.params?.source || route.name,
        });
    }, [navigation, restaurantPlace?.floors, route.name, route.params?.openMode, route.params?.source, selectTable, setAlreadySentCartItems, setDiscountsForWholeCart, t]);

    const layout = useMemo(() => {
        const horizontalPadding = width >= WINDOW_EXPANDED_MIN ? 28 : width >= WINDOW_MEDIUM_MIN ? 22 : 14;
        const cardWidth = width >= WINDOW_MEDIUM_MIN
            ? Math.min(width - (horizontalPadding * 2), 920)
            : width - (horizontalPadding * 2);

        return {
            horizontalPadding,
            cardWidth,
        };
    }, [width]);

    // Stable identities so OrderRow's React.memo holds across re-renders. The
    // previous inline `renderItem`/`onPress={() => handleOrderPress(item)}`
    // allocated fresh closures every render, re-rendering every visible card on
    // each poll/search/filter change. handleOrderPress already accepts the order,
    // so OrderRow (which calls onPress(order)) can take it directly.
    const keyExtractor = useCallback(
        (item) => item?._id || item?.orderCode || `${item?.createdAt}-${item?.tableId || 'order'}`,
        []
    );

    const renderItem = useCallback(({item}) => (
        <View style={[styles.rowWrap, {width: layout.cardWidth}]}>
            <OrderRow order={item} dense onPress={handleOrderPress} />
        </View>
    ), [handleOrderPress, layout.cardWidth, styles]);

    const screenCopy = useMemo(() => getScreenCopy(route.name, route.params, t), [route.name, route.params, t]);

    const headerComponent = useMemo(() => (
        <View style={[styles.headerCard, {width: layout.cardWidth}]}> 
            <Text style={styles.headerEyebrow}>{screenCopy.eyebrow}</Text>
            <Text style={styles.headerTitle}>{screenCopy.title}</Text>
            <Text style={styles.headerSubtitle}>{screenCopy.subtitle}</Text>
            <View style={styles.summaryRow}>
                <View style={styles.summaryPill}>
                    <Text style={styles.summaryPillText}>{t('orders_order_count', { count: visibleOrders.length })}</Text>
                </View>
                <View style={styles.secondaryPill}>
                    <Text style={styles.secondaryPillText}>{screenCopy.viewLabel}</Text>
                </View>
                {restaurantPlace?.name ? (
                    <View style={styles.locationPill}>
                        <Text style={styles.locationPillText} numberOfLines={1}>{restaurantPlace.name}</Text>
                    </View>
                ) : null}
            </View>
            <TextInput
                value={searchTerm}
                onChangeText={setSearchTerm}
                placeholder={t('orders_search_placeholder')}
                placeholderTextColor={theme.colors.placeholder}
                style={[styles.searchInput, isPhoneUi && styles.searchInputPhone]}
            />
            <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={styles.filterScrollContent}>
                {ORDER_TYPE_FILTERS.map((filterOption) => {
                    const isActive = selectedOrderType === filterOption.value;

                    return (
                        <TouchableOpacity
                            key={filterOption.value}
                            activeOpacity={0.85}
                            onPress={() => {
                                haptics.select();
                                setSelectedOrderType(filterOption.value);
                            }}
                            style={[styles.filterChip, isPhoneUi && styles.filterChipPhone, isActive && styles.filterChipActive]}
                        >
                            <Text style={[styles.filterChipText, isPhoneUi && styles.filterChipTextPhone, isActive && styles.filterChipTextActive]}>{t(filterOption.labelKey)}</Text>
                        </TouchableOpacity>
                    );
                })}
            </ScrollView>
        </View>
    ), [layout.cardWidth, restaurantPlace?.name, screenCopy.eyebrow, screenCopy.subtitle, screenCopy.title, screenCopy.viewLabel, searchTerm, selectedOrderType, visibleOrders.length, t, styles, theme, isPhoneUi]);

    const renderEmpty = useCallback(() => (
        <View style={[styles.emptyContainer, {width: layout.cardWidth}]}>
            <LottieView source={require('../../assets/animations/no-data-here.json')} autoPlay loop style={styles.animation} />
            <Text style={styles.emptyTitle}>{t('orders_empty_title')}</Text>
            <Text style={styles.emptySubtitle}>{t('orders_empty_subtitle')}</Text>
        </View>
    ), [layout.cardWidth, t, styles]);

    return (
        <LinearGradient colors={isDark ? [theme.colors.background, theme.colors.card, theme.colors.background] : ['#f5eadf', '#eadfcd', '#ddd6cf']} style={styles.background}>
            <FlatList
                style={styles.list}
                data={visibleOrders}
                keyExtractor={keyExtractor}
                renderItem={renderItem}
                ListHeaderComponent={headerComponent}
                ListEmptyComponent={renderEmpty}
                refreshing={refreshing}
                onRefresh={handleRefresh}
                showsVerticalScrollIndicator={false}
                removeClippedSubviews={Platform.OS === 'android'}
                initialNumToRender={8}
                maxToRenderPerBatch={8}
                updateCellsBatchingPeriod={50}
                windowSize={7}
                scrollEnabled
                nestedScrollEnabled
                keyboardShouldPersistTaps="handled"
                contentContainerStyle={[
                    styles.listContent,
                    visibleOrders.length === 0 && styles.listContentEmpty,
                    {
                        paddingHorizontal: layout.horizontalPadding,
                        paddingTop: 18,
                        paddingBottom: 120,
                    },
                ]}
            />
        </LinearGradient>
    );
}

const buildStyles = (theme) => ({
    background: {
        flex: 1,
        width: '100%',
        minHeight: 0,
    },
    list: {
        flex: 1,
        width: '100%',
        minHeight: 0,
    },
    listContent: {
        alignItems: 'center',
    },
    listContentEmpty: {
        flexGrow: 1,
        justifyContent: 'center',
    },
    headerCard: {
        width: '100%',
        backgroundColor: theme.colors.card,
        borderRadius: 24,
        padding: 18,
        marginBottom: 14,
        shadowColor: '#0f172a',
        shadowOffset: {width: 0, height: 8},
        shadowOpacity: 0.06,
        shadowRadius: 18,
        elevation: 3,
        borderWidth: 1,
        borderColor: theme.colors.border,
    },
    headerEyebrow: {
        fontSize: 11,
        fontWeight: '800',
        letterSpacing: 0.8,
        textTransform: 'uppercase',
        color: theme.colors.primary,
    },
    headerTitle: {
        marginTop: 6,
        fontSize: 22,
        fontWeight: '800',
        color: theme.colors.text,
    },
    headerSubtitle: {
        marginTop: 6,
        fontSize: 14,
        lineHeight: 20,
        color: theme.colors.subText,
    },
    summaryRow: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: 8,
        marginTop: 14,
    },
    summaryPill: {
        backgroundColor: '#fff7ed',
        borderRadius: 999,
        paddingHorizontal: 12,
        paddingVertical: 7,
    },
    summaryPillText: {
        color: '#c2410c',
        fontWeight: '800',
        fontSize: 12,
    },
    secondaryPill: {
        backgroundColor: theme.colors.inputBackground,
        borderRadius: 999,
        paddingHorizontal: 12,
        paddingVertical: 7,
    },
    secondaryPillText: {
        color: theme.colors.subText,
        fontWeight: '700',
        fontSize: 12,
    },
    locationPill: {
        maxWidth: '100%',
        backgroundColor: '#eef2ff',
        borderRadius: 999,
        paddingHorizontal: 12,
        paddingVertical: 7,
    },
    locationPillText: {
        color: '#4338ca',
        fontWeight: '700',
        fontSize: 12,
    },
    searchInput: {
        marginTop: 12,
        height: 42,
        borderRadius: 12,
        borderWidth: 1,
        borderColor: theme.colors.border,
        paddingHorizontal: 12,
        fontSize: 14,
        color: theme.colors.text,
        backgroundColor: theme.colors.inputBackground,
    },
    filterScrollContent: {
        marginTop: 10,
        paddingBottom: 2,
        paddingRight: 6,
        gap: 8,
    },
    filterChip: {
        paddingHorizontal: 14,
        paddingVertical: 9,
        borderRadius: 999,
        backgroundColor: theme.colors.inputBackground,
        borderWidth: 1,
        borderColor: 'transparent',
    },
    searchInputPhone: {
        height: 52,
        fontSize: 16,
        borderRadius: 14,
    },
    filterChipPhone: {
        minHeight: 48,
        justifyContent: 'center',
        paddingHorizontal: 18,
    },
    filterChipTextPhone: {
        fontSize: 15,
    },
    filterChipActive: {
        backgroundColor: theme.colors.primary,
        borderColor: '#e7a22c',
    },
    filterChipText: {
        fontSize: 13,
        fontWeight: '700',
        color: theme.colors.subText,
    },
    filterChipTextActive: {
        color: '#111827',
    },
    rowWrap: {
        width: '100%',
        alignSelf: 'center',
        maxWidth: '100%',
    },
    emptyContainer: {
        width: '100%',
        alignItems: 'center',
        justifyContent: 'center',
        paddingVertical: 42,
    },
    animation: {
        width: 160,
        height: 160,
        alignSelf: 'center',
    },
    emptyTitle: {
        marginTop: 10,
        fontSize: 20,
        fontWeight: '800',
        textAlign: 'center',
        color: theme.colors.text,
    },
    emptySubtitle: {
        marginTop: 8,
        maxWidth: 420,
        textAlign: 'center',
        fontSize: 14,
        lineHeight: 20,
        color: theme.colors.subText,
    },
});